import { useState, useMemo, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import Table, { TableHeading, TableControls, TableFooter } from "@/components/Table";
import { Table as TableComponent } from "@tanstack/react-table";
import Button from "@/components/Button";
import SearchInput from "@/components/SearchInput";
import EmptyState from "@/components/EmptyState";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import { StudentRow } from "../types";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { useSearchWithParams, useParams as useParamsHook } from "@/hooks/useSearchParams";
import MultiSelect from "@/components/MultiSelect";
import pluralize from "@/lib/pluralize";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import useModal from "@/hooks/useModal";
import Banner from "@/components/Banner";
import { showSnackbar } from "@/lib/snackbarUtils";
import { buildColumns, mapGraduationRequestsFromUniversities, isEligibleForGraduation } from "./_utils";
import { getStatusTagColor } from "../../_utils";
import { buildProgramLevelColumns } from "../../../_shared/_utils";
import { APPROVE_GRADUATION_REQUESTS_MUTATION, REJECT_GRADUATION_REQUESTS_MUTATION } from "./mutations";
import { GET_PENDING_GRADUATION_REQUESTS_QUERY } from "./queries";
import { PendingGraduationRequestsQuery } from "./types";
import { useUniversityStaffPermissions } from "@/hooks/useUniversityStaffPermissions";
import useCapabilities from "@/hooks/useCapabilities";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import {
  FILTER_PROGRAM_LEVEL_OPTIONS,
  PAT_STATUS_OPTIONS,
  convertPATStatusToUppercase
} from "../../../_shared/constants";
import { parseProgramLevelFilterSelections } from "../../../_shared/programLevelFilter";

export default function GraduationEligibleStudentsPage() {
  const { universityId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { rowSelection, setRowSelection, bulkSelectionProps, isSelectedOnly, deselectAll } = useBulkSelection();
  const rowSelectionRef = useRef<typeof rowSelection>({});
  const { isCompactLayout } = useIsCompactLayout();
  const { Menu, setMenu } = useTableRowMenu();
  const { params, addSearchParam, deleteSearchParams, searchParamsUrl } = useParamsHook();
  const [selectedPrograms, setSelectedPrograms] = useState<string[]>(
    params.programs ? (Array.isArray(params.programs) ? params.programs : [params.programs]) : []
  );
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "searchTerm"
  });
  const [selectedPatStatus, setSelectedPatStatus] = useState<string[]>(
    params.status
      ? Array.isArray(params.status)
        ? params.status.map((s) => convertPATStatusToUppercase(s))
        : [convertPATStatusToUppercase(params.status)]
      : []
  );
  const [selectedStudent, setSelectedStudent] = useState<StudentRow | null>(null);
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(false);
  const [isGraduationDialogOpen, setIsGraduationDialogOpen] = useState(false);
  const { Modal } = useModal();
  const universityStaffPermissions = useUniversityStaffPermissions();
  const { hasRole } = useCapabilities();
  const canFinalizeGraduations = hasRole(ROLE.EDUCATION_ADMIN) || hasRole(ROLE.GLOBAL_ADMIN);
  const [error, setError] = useState<string | null>(null);

  // Keep rowSelectionRef in sync with rowSelection
  rowSelectionRef.current = rowSelection;

  const { data, loading, refetch } = useQuery<PendingGraduationRequestsQuery>(GET_PENDING_GRADUATION_REQUESTS_QUERY, {
    variables: { legacyId: universityId }
    // ...paginationVariables
  });

  const allStudents = useMemo(() => mapGraduationRequestsFromUniversities(data || {}), [data]);

  const selectedStudents = useMemo(() => {
    if (!isSelectedOnly) return [];
    const selectedIds = Object.keys(rowSelection);
    return allStudents.filter((student) => selectedIds.includes(student.id));
  }, [allStudents, rowSelection, isSelectedOnly]);

  const filteredStudents = useMemo(() => {
    const studentsToFilter = isSelectedOnly ? selectedStudents : allStudents;

    return studentsToFilter.filter((student) => {
      if (debouncedSearch) {
        const searchLower = debouncedSearch.toLowerCase();
        const matchesSearch =
          student.name?.toLowerCase().includes(searchLower) ||
          student.pgaId?.toLowerCase().includes(searchLower) ||
          student.email?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      if (selectedPrograms.length > 0) {
        const { curriculumVersions, levels } = parseProgramLevelFilterSelections(selectedPrograms);
        const matchesProgram =
          (!curriculumVersions || curriculumVersions.includes(student.program?.version)) &&
          (!levels || levels.includes(Number(student.program?.level)));
        if (!matchesProgram) return false;
      }

      if (selectedPatStatus.length > 0) {
        const studentPatStatusUppercase = convertPATStatusToUppercase(student.patStatus);
        const matchesPatStatus = selectedPatStatus.some((status) => status === studentPatStatusUppercase);
        if (!matchesPatStatus) return false;
      }

      return true;
    });
  }, [isSelectedOnly, selectedStudents, allStudents, debouncedSearch, selectedPrograms, selectedPatStatus]);

  const sortedStudents = useMemo(() => {
    return [...filteredStudents].sort((studentA, studentB) => {
      const studentAEligible = isEligibleForGraduation(studentA);
      const studentBEligible = isEligibleForGraduation(studentB);

      // Sort ineligible students to the top
      if (!studentAEligible && studentBEligible) return -1;
      if (studentAEligible && !studentBEligible) return 1;

      // Maintain original order for students with same eligibility status
      return 0;
    });
  }, [filteredStudents]);

  const students = sortedStudents;

  const eligibilityStats = useMemo(() => {
    const total = students.length;
    const eligible = students.filter(isEligibleForGraduation).length;
    const ineligible = total - eligible;

    return { total, eligible, ineligible };
  }, [students]);

  const handleViewStudent = useCallback((student: StudentRow) => {
    setSelectedStudent(student);
    setIsDetailsPanelOpen(true);
  }, []);

  const [approveGraduationRequests] = useMutation(APPROVE_GRADUATION_REQUESTS_MUTATION);
  const [rejectGraduationRequests] = useMutation(REJECT_GRADUATION_REQUESTS_MUTATION);

  const handleRemoveFromEligible = useCallback(
    async (graduationRequestId?: string) => {
      try {
        let graduationRequestIds: string[];

        if (graduationRequestId) {
          graduationRequestIds =
            data?.universities?.[0]?.graduationRequests
              ?.filter((req) => req.id === graduationRequestId)
              ?.map((req) => req.id) || [];
        } else {
          // Get current rowSelection at the time of execution using ref
          const currentRowSelection = Object.keys(rowSelectionRef.current).filter(Boolean);
          graduationRequestIds = currentRowSelection.length > 0 ? currentRowSelection : [];
        }

        if (graduationRequestIds.length === 0) {
          setError("No graduation requests found to remove");
          return;
        }

        const { data: mutationData } = await rejectGraduationRequests({
          variables: { input: { graduationRequestIds } }
        });

        if (mutationData?.rejectGraduationRequests?.success) {
          const selectedCount = graduationRequestIds.length;
          showSnackbar(`${selectedCount} ${pluralize(selectedCount, "student")} moved back to Current Students`);
          deselectAll();
          refetch();
        } else {
          setError(mutationData?.rejectGraduationRequests?.message || "Error removing students");
        }
      } catch (error) {
        console.error("Error removing students from eligible:", error);
        setError("Error removing students");
      }
    },
    [data, rejectGraduationRequests, refetch, deselectAll]
  );

  const columns = useMemo(
    () =>
      buildColumns({
        isCompactLayout,
        hasCheckbox: true,
        navigate,
        location,
        searchParamsUrl,
        universityId: universityId || "",
        handleViewStudent,
        setMenu,
        actionId,
        handleRemoveFromEligible
      }),
    [
      isCompactLayout,
      navigate,
      location,
      searchParamsUrl,
      universityId,
      handleViewStudent,
      setMenu,
      handleRemoveFromEligible
    ]
  );

  const programColumns = buildProgramLevelColumns(getStatusTagColor);

  const handleMarkEligibleForGraduation = async () => {
    try {
      const selectedRequestIds = Object.keys(rowSelectionRef.current).filter(Boolean);

      let graduationRequestIds: string[];

      if (selectedRequestIds.length === 0) {
        graduationRequestIds = allStudents.map((student) => student.id);
      } else {
        graduationRequestIds = selectedRequestIds;
      }

      if (graduationRequestIds.length === 0) {
        setError("Please select at least one student");
        return;
      }

      const { data: mutationData } = await approveGraduationRequests({
        variables: { input: { graduationRequestIds } }
      });

      if (mutationData?.approveGraduationRequests?.success) {
        const count = graduationRequestIds.length;
        showSnackbar(`${count} ${pluralize(count, "student")} graduated`);
        setIsGraduationDialogOpen(false);
        deselectAll();
        refetch();
      } else {
        setError(mutationData?.approveGraduationRequests?.message || "Error approving graduation requests");
      }
    } catch (error) {
      console.error("Error approving graduation requests:", error);
      setError("Error approving graduation requests");
    }
  };

  const handleCloseGraduationDialog = () => {
    setIsGraduationDialogOpen(false);
    setError(null);
  };

  const selectedCount = useMemo(() => Object.keys(rowSelection).length, [rowSelection]);
  const areStudentsSelected = useMemo(() => selectedCount > 0, [selectedCount]);
  const totalStudents = useMemo(() => data?.universities?.[0]?.graduationRequests?.length || 0, [data]);
  const studentsToFinalizeCount = useMemo(
    () => (areStudentsSelected ? selectedCount : totalStudents),
    [areStudentsSelected, selectedCount, totalStudents]
  );

  const activeFiltersCount = useMemo(() => {
    return (selectedPrograms.length > 0 ? 1 : 0) + (selectedPatStatus.length > 0 ? 1 : 0);
  }, [selectedPrograms, selectedPatStatus]);

  const filterState = {
    programs: selectedPrograms.length > 0,
    status: selectedPatStatus.length > 0
  };

  const handleClearAllFilters = () => {
    setSelectedPrograms([]);
    setSelectedPatStatus([]);
    clearSearch();
    deleteSearchParams(["programs[]", "status[]", "searchTerm"]);
  };

  return (
    <>
      <TableHeading
        heading="PGM Program Students Eligible for Graduation"
        body="PGA National shall review and finalize the graduation status for all eligible students associated with this PGM University Program."
        actions={
          eligibilityStats.eligible > 0 && canFinalizeGraduations
            ? [
                {
                  label: "Finalize Graduations",
                  variant: "filled" as const,
                  onClick: () => setIsGraduationDialogOpen(true),
                  testId: "open-graduation-dialog-button"
                }
              ]
            : []
        }
      />
      <Table
        data={students}
        loading={loading}
        columns={columns}
        enableRowSelection={true}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        onClickRow={(row) => {
          const returnUrl = encodeURIComponent(`${location.pathname}${location.search}`);
          const separator = searchParamsUrl ? "&" : "";
          navigate(
            `/education/universities/${universityId}/students/${row.original.pgaId}?${searchParamsUrl}${separator}returnUrl=${returnUrl}`
          );
        }}
        data-testid="eligible-students-table"
        renderExpandedRow={({ row }) => {
          const levels = row.original.programLevels || [];
          return (
            <TableDetailsPanel data={levels} columns={programColumns} title={`Program Levels (${levels.length})`} />
          );
        }}
        hasActiveFilters={searchValue || activeFiltersCount > 0}
        renderNoDataEmptyState={() => (
          <EmptyState title="No Students Eligible for Graduation." caption="" iconName="school" />
        )}
        renderControls={(table: TableComponent<StudentRow>) => {
          return (
            <>
              {error && (
                <div className="mb-4">
                  <Banner
                    variant="error"
                    description={error}
                    inline={true}
                    closable={true}
                    onClose={() => setError(null)}
                    data-testid="banner"
                  />
                </div>
              )}
              <TableControls
                useExpandedFilters={true}
                expandedFiltersState={filterState}
                onClearAllFilters={handleClearAllFilters}
                activeFilters={activeFiltersCount}
                bulkActions={[
                  {
                    label: "Remove from Eligible for Graduation",
                    variant: "outlined" as const,
                    onClick: () => handleRemoveFromEligible(),
                    testId: "remove-graduation-button"
                  }
                ]}
                {...bulkSelectionProps}
                onDeselectAll={deselectAll}
                totalRowCount={selectedCount}
                onToggleAllRows={(checked) => table.toggleAllRowsSelected(checked)}
                allRowsSelected={table.getIsAllRowsSelected()}
                someRowsSelected={table.getIsSomeRowsSelected()}
              >
                <SearchInput
                  placeholder="Search Students"
                  query={searchValue}
                  onClear={clearSearch}
                  onChange={setSearchValue}
                />
                <div data-name="programs">
                  <MultiSelect
                    label="Program / Level"
                    options={FILTER_PROGRAM_LEVEL_OPTIONS}
                    selectedValues={selectedPrograms}
                    onSelect={(value, checked) => {
                      const updatedPrograms = checked
                        ? [...selectedPrograms, value]
                        : selectedPrograms.filter((v) => v !== value);

                      setSelectedPrograms(updatedPrograms);

                      addSearchParam("programs[]", updatedPrograms.join(","));
                    }}
                    onClear={() => {
                      setSelectedPrograms([]);
                      deleteSearchParams("programs[]");
                    }}
                  />
                </div>
                <div data-name="status">
                  <MultiSelect
                    label="PAT Status"
                    options={PAT_STATUS_OPTIONS}
                    className="w-full"
                    selectedValues={selectedPatStatus}
                    onSelect={(value, checked) => {
                      const updatedStatus = checked
                        ? [...selectedPatStatus, value]
                        : selectedPatStatus.filter((v) => v !== value);

                      setSelectedPatStatus(updatedStatus);

                      addSearchParam("status[]", updatedStatus.join(","));
                    }}
                    onClear={() => {
                      setSelectedPatStatus([]);
                      deleteSearchParams("status[]");
                    }}
                  />
                </div>
              </TableControls>
            </>
          );
        }}
        renderFooter={() => <TableFooter {...bulkSelectionProps} totalRowCount={totalStudents} />}
      />

      <Menu />

      <PersonSideSheet
        open={isDetailsPanelOpen && !!selectedStudent}
        onClose={() => setIsDetailsPanelOpen(false)}
        pgaId={selectedStudent?.pgaId ?? null}
        title="Student Details"
        showPersonalDetails={true}
        universityName={selectedStudent?.university?.name}
        customActionButtons={
          selectedStudent
            ? [
                {
                  text: "View PGM Details",
                  variant: "outlined" as const,
                  onClick: () => {
                    const returnUrl = encodeURIComponent(`${location.pathname}${location.search}`);
                    const separator = searchParamsUrl ? "&" : "";
                    navigate(
                      `/education/universities/${universityId}/students/${selectedStudent.pgaId}?${searchParamsUrl}${separator}returnUrl=${returnUrl}`
                    );
                  }
                },
                ...(universityStaffPermissions?.shouldShowPersonProfileActions
                  ? [
                      {
                        text: "View Full Profile",
                        variant: "text" as const,
                        onClick: () => {
                          window.open(`/people/${selectedStudent.pgaId}/person/overview`, "_blank");
                        }
                      }
                    ]
                  : [])
              ]
            : undefined
        }
      />

      {isGraduationDialogOpen && (
        <Modal
          onClosed={handleCloseGraduationDialog}
          hasFormElements={false}
          displayOverflow
          headline="Finalize Graduations"
          action={
            <div className="flex gap-2">
              <Button
                variant="filled"
                onClick={() => void handleMarkEligibleForGraduation()}
                testId="finalize-graduation-button"
              >
                Finalize {studentsToFinalizeCount} {pluralize(studentsToFinalizeCount, "Student")}
              </Button>
            </div>
          }
        >
          <div>
            {error && (
              <div className="mb-4">
                <Banner
                  variant="error"
                  description={error}
                  inline={true}
                  closable={true}
                  onClose={() => setError(null)}
                />
              </div>
            )}
            <div className="font-label-large font-medium text-sys-brand-on-surface mb-4">
              Total Students to Finalize Graduations: {studentsToFinalizeCount}
            </div>
            <div className="text-body-medium text-sys-brand-on-surface">
              {areStudentsSelected ? (
                <>
                  Are you sure you want to mark {selectedCount} selected {pluralize(selectedCount, "student")} as
                  graduated?
                </>
              ) : (
                <>
                  Are you sure you want to mark all {totalStudents} {pluralize(totalStudents, "student")} as graduated?
                </>
              )}{" "}
              Please ensure that the {pluralize(studentsToFinalizeCount, "student")}
              {studentsToFinalizeCount === 1 ? " has" : " have"} met all program requirements. Four years of college
              credit will be added to their profiles, and their student membership bands will be ended, making them
              eligible for full membership. The {pluralize(studentsToFinalizeCount, "student")} will continue to be
              visible on the Alumni page and will be notified about next steps.
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
